import {
  Icon,
  Text,
  Input,
  Link,
  Grid,
  Space,
  Flex,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalContent,
  Modal,
  ModalOverlay,
  Box,
} from '@tonic-one/react';

import { WarningCircleIcon } from '@tonic-one/react-icons';

import { ProvisionStatusEnum } from '@Types/provision';
import ProvisionModal from '../../Dashboard/ProvisionModal';
import ReAuthModal from '../../Dashboard/ReAuthModal/reAuthModal';
import DeprovisionModal from './Deprovision/deprovisionModal';
import { SaModalNameEnum, SaModalState } from './types';
import TeamsChatModal from '../../Dashboard/ProvisionModal/teamsChatModal';
import { Trans, useTranslation } from 'react-i18next';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import ActionModal from '@Components/Common/ActionModal';
import { AdministrationApi, PocApi, PolicyApi, ProvisionApi } from '@Apis';
import {
  AppForSa,
  DeProvisionStatus,
  DeprovisionDetail,
} from '@Types/administrator';
import LoadingBtn from '../../../components/Common/LoadingBtn';
import TeamsChatUpdateSecretOrChangeAppModal from './TeamsChatModal';
import AdvancedTooltipModal from 'src/pages/Dashboard/AdvanceModal';
import { PolicyTypeEnum } from '@Policy/utils/constants';
import { isInUIC } from '@Utils/common';
import {
  AdvancedFeatureTypeEnum,
  AdvancedFeatureInfo,
  Service,
} from '@Types/app';
import {
  CAS_DOC_LINK,
  THIRD_PARTY_LINK,
  getCasOLHLink,
} from '../../../utils/casDocLink';
import { SpodMigrationModal } from './SpodMigrationModal';
import i18next from 'i18next';

const SAModal = memo(
  ({
    saModalState,
    onClose,
    refreshData,
  }: {
    saModalState: Exclude<SaModalState, null>;
    onClose: () => void;
    refreshData: () => void;
  }) => {
    const { name, data } = saModalState;
    const [advanceData, setAdvanceData] = useState<AdvancedFeatureInfo>({
      needConfirm: true,
      avaliableCreit: 0,
      aadUserCount: 0,
    });
    const [isShowAdvancedModal, setIsShowAdvancedModal] = useState(false);
    const [isShowProvisionModal, setIsShowProvisionModal] = useState(false);
    const onConfirm = useCallback(() => {
      setIsShowAdvancedModal(false);
      setIsShowProvisionModal(true);
    }, []);

    useEffect(() => {
      if (
        isInUIC() &&
        saModalState.name === SaModalNameEnum.Provision &&
        (saModalState.data.app === AppForSa.ExchangeInline ||
          saModalState.data.app === AppForSa.GmailInline)
      ) {
        PolicyApi.getAdvancedFeatureInfo(
          AdvancedFeatureTypeEnum.provision,
          saModalState.data.app === AppForSa.ExchangeInline
            ? PolicyTypeEnum.ExchangeInline
            : PolicyTypeEnum.GmailInline
        )
          .then((res) => {
            if (res.data?.needConfirm) {
              setAdvanceData(res.data);
              setIsShowAdvancedModal(true);
            } else {
              setIsShowProvisionModal(true);
            }
          })
          .catch((error) => {
            console.log(JSON.stringify(`error: ${error}`));
          });
      } else {
        setIsShowProvisionModal(true);
      }
    }, [saModalState.data]);

    if (name === SaModalNameEnum.Deprovision) {
      return (
        <DeprovisionModal
          {...data}
          onClose={onClose}
          refreshData={refreshData}
        />
      );
    }

    if (name === SaModalNameEnum.ReAuth) {
      return (
        <ReAuthModal
          modalState={data}
          onClose={onClose}
          refreshData={refreshData}
        />
      );
    }

    if (name === SaModalNameEnum.Provision) {
      return (
        <>
          <AdvancedTooltipModal
            isVisible={isShowAdvancedModal}
            onClose={onClose}
            onConfirm={onConfirm}
            advanceData={advanceData}
            advanceFeatureType={AdvancedFeatureTypeEnum.provision}
          ></AdvancedTooltipModal>
          <ProvisionModal
            modalState={{ serviceType: data.app as unknown as Service }}
            orgId={data.orgId}
            isOpen={isShowProvisionModal}
            onClose={() => {
              onClose();
              refreshData();
            }}
          />
        </>
      );
    }

    if (name === SaModalNameEnum.ProtectOwnApp) {
      return (
        <TeamsChatModal
          orgId={data.orgId}
          onClose={() => {
            onClose();
            refreshData();
          }}
          isOpen={true}
          provisionStatus={{
            status: ProvisionStatusEnum.NotStarted,
          }}
          isProtectOwnApp={true}
        />
      );
    }

    if (name === SaModalNameEnum.UpdateSecretOrChangeApp) {
      return (
        <TeamsChatUpdateSecretOrChangeAppModal
          orgId={data.orgId}
          appId={data.appId}
          onClose={() => {
            onClose();
            refreshData();
          }}
        />
      );
    }

    if (name === SaModalNameEnum.Extend) {
      return (
        <ExchangeServerExtendModal
          orgId={data.orgId}
          onClose={onClose}
          refreshData={refreshData}
        />
      );
    }

    if (name === SaModalNameEnum.Details) {
      return (
        <DeprovisionDetailModal
          app={data.app}
          orgId={data.orgId}
          status={data.status}
          onClose={onClose}
          refreshData={refreshData}
        />
      );
    }

    if (name === SaModalNameEnum.ChangePassword) {
      return (
        <ChangePasswordModal
          email={data.email}
          orgId={data.orgId}
          onClose={onClose}
        />
      );
    }

    if (name === SaModalNameEnum.NeedLicense) {
      return <NeedLicenseModal onClose={onClose} app={data.app} />;
    }

    if (name === SaModalNameEnum.SpodMigration) {
      return <SpodMigrationModal onClose={onClose} orgId={data.orgId} />;
    }

    if (name === SaModalNameEnum.CleanUp) {
      return <CleanUpDelegatorAccount onClose={onClose} orgId={data.orgId} />;
    }

    if (name === SaModalNameEnum.PocExtend) {
      return (
        <PocExtend
          onClose={onClose}
          orgId={data.orgId}
          refreshData={refreshData}
        />
      );
    }
    return <></>;
  }
);

export default SAModal;

const ExchangeServerExtendModal = ({
  orgId,
  onClose,
  refreshData,
}: {
  orgId: string;
  onClose: () => void;
  refreshData: () => void;
}) => {
  const { t } = useTranslation('administration');

  const onClick = async () => {
    try {
      await AdministrationApi.apiExtendExchangeServerExpiration(orgId);
      window.showNotification({
        severity: 'success',
        titleMsg: t('administration.sa.table.status.tip.extendSuccessfully'),
      });
      refreshData();
    } catch {
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.unknownError'),
      });
    } finally {
      onClose();
    }
  };
  return (
    <ActionModal
      isOpen={true}
      body={t('administration.sa.table.status.tip.extendDesc')}
      onConfirm={onClick}
      onClose={onClose}
    />
  );
};

const DeprovisionDetailModal = ({
  app,
  orgId,
  status,
  onClose,
  refreshData,
}: {
  app: AppForSa;
  orgId: string;
  onClose: () => void;
  status: DeProvisionStatus.Failure | DeProvisionStatus.DeProvisioning;
  refreshData: () => void;
}) => {
  const { t } = useTranslation(['administration', 'common']);
  const [details, setDetails] = useState<DeprovisionDetail[]>([]);
  const [isNestedOpen, setNestedModalOpen] = useState(false);

  const onForceDelete = async () => {
    try {
      await ProvisionApi.apiForceDeprovision(orgId, app);
      window.showNotification({
        severity: 'success',
        titleMsg: t('administration.sa.table.status.tip.deleteSaSuccessfully'),
      });
      refreshData();
    } catch {
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.unknownError'),
      });
    } finally {
      setNestedModalOpen(false);
      onClose();
    }
  };

  useEffect(() => {
    AdministrationApi.apiGetAppDeprovisionStatus(orgId, app)
      .then((res) => {
        if (res.data.tasks && Array.isArray(res.data.tasks)) {
          setDetails(res.data.tasks);
        } else {
          console.error(`tasks not an array, tasks: ${res.data.tasks}`);
          setDetails([]);
        }
      })
      .catch((e) => {
        console.error(e);
        setDetails([]);
      });
  }, [orgId, app]);

  return (
    <>
      <Modal
        ensureFocus={true}
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={true}
        onClose={onClose}
        size="md"
      >
        <ModalOverlay opacity={0.9} />
        <ModalContent>
          <ModalHeader>
            {t(`administration.sa.deprovision.${app}.title`)}
          </ModalHeader>
          <ModalBody>
            <Table width="100%">
              <TableBody>
                {details.map((detail, index) => (
                  <TableRow key={detail.key} width="100%">
                    <TableCell width="10%">{index + 1}</TableCell>
                    <TableCell width="70%">
                      {t(
                        `administration.sa.deprovision.details.uiStatus.${detail.key}`
                      )}
                    </TableCell>
                    <TableCell width="20%">
                      {detail.status.includes('FAILURE')
                        ? t('common:common.label.failed')
                        : t('common:common.label.inProgress')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {status === DeProvisionStatus.DeProvisioning ? (
              // TODO 预估时间
              <Text pt="20px"></Text>
            ) : (
              <Flex pt="20px" gap="8px" direction="column">
                <Text>
                  <Trans
                    ns="administration"
                    i18nKey="administration.sa.deprovision.details.desc"
                  >
                    placeholder
                    <Link
                      onClick={() => {
                        setNestedModalOpen(true);
                      }}
                    >
                      placeholder
                    </Link>
                    placeholder
                  </Trans>
                </Text>
                <Text>
                  <Trans
                    ns="administration"
                    i18nKey="administration.sa.deprovision.details.contact"
                  >
                    placeholder
                    <Link
                      onClick={() => {
                        window.open(
                          getCasOLHLink(
                            CAS_DOC_LINK.deprovision.contact,
                            i18next.language
                          ),
                          '_blank'
                        );
                      }}
                    >
                      placeholder
                    </Link>
                    placeholder
                  </Trans>
                </Text>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common:common.action.label.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        closeOnEsc
        closeOnOutsideClick
        isClosable
        isOpen={isNestedOpen}
        onClose={() => setNestedModalOpen(false)}
        size="xs"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalBody>
            <Icon as={WarningCircleIcon} color="yellow:50" mr="8px" />
            {t('administration.sa.deprovision.details.forceRemove')}
          </ModalBody>
          <ModalFooter columnGap="2x">
            <Button
              variant="primary"
              onClick={() => onForceDelete()}
              minWidth="20x"
            >
              {t('common.button.ok')}
            </Button>
            <Button onClick={() => setNestedModalOpen(false)} minWidth="20x">
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const ChangePasswordModal = ({
  email,
  orgId,
  onClose,
}: {
  email: string;
  orgId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['administration', 'common']);
  const ref = useRef<any>(null);
  const onUpdate = async () => {
    try {
      const res = await AdministrationApi.apiChangDeprecatedSpodPassword(
        orgId,
        email,
        ref.current.value
      );
      if (res.data?.status === 'failure') {
        window.showNotification({
          severity: 'error',
          titleMsg: t([
            `errors:errors.${res.data?.reason}`,
            'errors:errors.internalError',
          ]),
        });
      } else {
        window.showNotification({
          severity: 'success',
          titleMsg: t(
            'administration:administration.sa.table.status.tip.updatedPwdSuccessfully'
          ),
        });
      }
    } catch {
      window.showNotification({
        severity: 'error',
        titleMsg: t('errors:errors.internalError'),
      });
    } finally {
      onClose();
    }
  };
  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={true}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>
          {t('administration.sa.table.status.btn.changePwd')}
        </ModalHeader>
        <ModalBody>
          <Grid
            templateColumns="max-content auto"
            gap="16px"
            alignItems="center"
            mt="20px"
          >
            <Text> {t('common:common.label.email')}:</Text>
            <Text>{email}</Text>
            <Flex align="center">
              <Text> {t('common:common.label.password')}:</Text>
            </Flex>
            <Input
              placeholder={t('common:common.label.password')}
              ref={ref}
              type="password"
            />
          </Grid>
        </ModalBody>
        <ModalFooter>
          <LoadingBtn minWidth="20x" onClick={onUpdate}>
            {t('common:common.action.label.update')}
          </LoadingBtn>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common:common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const CleanUpDelegatorAccount = ({
  orgId,
  onClose,
}: {
  orgId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['administration', 'common', 'errors']);
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const onCleanUp = async () => {
    try {
      await ProvisionApi.apiCleanUpSpodAccount(email, pwd, orgId);
      onClose();
    } catch {
      window.showNotification({
        severity: 'error',
        titleMsg: t('errors:errors.internalError'),
      });
    }
  };
  const onVerify = async () => {
    try {
      const data = (
        await ProvisionApi.apiVerifiedSpodAccount(email, pwd, orgId)
      ).data;
      if (data.status === 'success') {
        setIsVerified(true);
      } else {
        window.showNotification({
          severity: 'error',
          titleMsg: t('errors:errors.administrator.invalidAccountCredentials'),
        });
      }
    } catch {
      window.showNotification({
        severity: 'error',
        titleMsg: t('errors:errors.internalError'),
      });
    }
  };
  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={true}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>
          {t('administration.sa.table.status.btn.changePwd')}
        </ModalHeader>
        <ModalBody>
          <Text>
            {t(
              'administration:administration.sa.table.status.tip.provideO365Credentials'
            )}
          </Text>
          <Grid
            templateColumns="max-content auto"
            gap="16px"
            alignItems="center"
            mt="20px"
          >
            <Text> {t('common:common.label.email')}:</Text>
            <Input
              onChange={(e) => {
                setEmail(e.target.value);
                setIsVerified(false);
              }}
              placeholder={t('common:common.label.email')}
              type="text"
              maxLength={256}
            />

            <Text> {t('common:common.label.password')}:</Text>
            <Flex gap="12px">
              <Input
                onChange={(e) => {
                  setPwd(e.target.value);
                  setIsVerified(false);
                }}
                placeholder={t('common:common.label.password')}
                type="password"
                maxLength={256}
              />
              <Button
                variant="primary"
                onClick={onVerify}
                disabled={email === '' || pwd === ''}
              >
                {t('common:common.action.label.verify')}
              </Button>
            </Flex>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <LoadingBtn minWidth="20x" onClick={onCleanUp} disabled={!isVerified}>
            {t('common:common.label.done')}
          </LoadingBtn>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common:common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const NeedLicenseModal = ({
  onClose,
  app,
}: {
  onClose: () => void;
  app: AppForSa.ExchangeInline | AppForSa.GmailInline;
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={true}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(`provision:provision.service.${app}.title`)}
        </ModalHeader>
        <ModalBody>
          <Text>
            {t(`provision:provision.service.${app}.needLicense`)}{' '}
            <Link href={THIRD_PARTY_LINK.contactReseller} target="_blank">
              {t('common:common.action.label.clickHere')}
            </Link>
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" minWidth="20x" onClick={onClose}>
            {t('common:common.label.done')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const PocExtend = ({
  onClose,
  orgId,
  refreshData,
}: {
  onClose: () => void;
  orgId: string;
  refreshData: () => void;
}) => {
  const { t } = useTranslation();

  const onSubmit = async () => {
    try {
      await PocApi.pocUpgrade(orgId);
    } catch (err) {
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.unknownError'),
      });
      console.error(err);
    }
    onClose();
    refreshData();
  };

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={true}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('administration:administration.sa.pocExtend.header')}
        </ModalHeader>
        <ModalBody>
          <Box
            dangerouslySetInnerHTML={{
              __html: t('administration.sa.pocExtend.step1'),
            }}
          />
          <Box
            my="12px"
            dangerouslySetInnerHTML={{
              __html: t('administration.sa.pocExtend.step2'),
            }}
          />
          <Box
            dangerouslySetInnerHTML={{
              __html: t('administration.sa.pocExtend.step3'),
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" minWidth="20x" onClick={onSubmit}>
            {t('common:common.action.label.submit')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common:common.action.label.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
